// CHAT-LATE-WRITES-NO-TENANT-GUARD-1 — the chat package's copy of the canonical
// "drop a reply that outlived its tenant" writer.
//
// WHY CHAT NEEDED ONE AT ALL. Registering a key as workspace-scoped (the
// shell's state-app.js) wipes it ON the switch. It cannot stop a request
// ISSUED in workspace A from writing its reply into workspace B afterwards.
// Chat is the package with the most of those: every channel loader, every
// membership mutation, every message send and every attachment upload writes
// a workspace-scoped key AFTER an `await`. Before this module the package had
// zero guards, so a switch mid-upload left `{ [A-channelId]: [] }` sitting in
// workspace B's `pendingAttachments`, and a slow channel fetch repopulated B's
// sidebar with A's channels.
//
// DROPPING is right rather than rerouting or retrying: the payload describes a
// workspace the user is no longer looking at, and PHASE-1 of the switch has
// already restored the correct empty for the one they are.
//
// ⛔ CAPTURE BEFORE THE AWAIT. `sameWorkspaceWriter(root)` must be called on
// the SAME tick as the request, before the first `await` — it snapshots the
// workspace id at call time. Calling it inside the `.then()` snapshots the NEW
// workspace and the guard can never fire.
//
// ⛔ TWO SHAPES, ONE PRIMITIVE. Most chat writes are patches, so they take
// `sameWorkspaceWriter(root)` and call `_write({...})`. `messagesByChannel` is
// NOT written as a patch — `chatUtils.setChannelMessages` assigns the nested
// per-channel key directly so one channel's arrival does not re-fire every
// other channel's subscribers. Those call sites capture `wsIdOf(root)` before
// the await instead and hand the token to `setChannelMessages` /
// `deleteChannelMessages`, which do the same comparison this file's writer
// does. Same module, same snapshot rule, one comparison per shape.
//
// ⛔ WHY THIS FILE EXISTS THREE TIMES. The shell owns the canonical copy at
// workspace/functions/sameWorkspaceWriter.js and `tickets` keeps a second one.
// `chat` is a SUB-APP and must not import the shell either, so it keeps this
// third one, held byte-identical below the comments by
// __tests__/chatLateWriteTenantScope.test.js's drift pin (it strips comments
// and compares code, so documenting each copy in its own words is not a
// drift). Consumers inside this package import THIS module — never re-inline it.

// Both id shapes are live on root: `workspace.id` from the SDK's workspace
// payload and `workspace._id` from the raw Mongo document. Stringified so a
// numeric id and its string form never read as two different tenants.
export const wsIdOf = (root) => String(root?.workspace?.id || root?.workspace?._id || '')

// Returns a `write(patch)` that applies the patch only while the workspace is
// still the one it was created in. Returns `true` when the patch landed and
// `false` when it was dropped, so a caller can tell the two apart (a silent
// no-op reads as success at the call site).
export const sameWorkspaceWriter = (root) => {
  // Keep the registered function self-contained when Frank revives its source.
  const idOf = (state) => String(state?.workspace?.id || state?.workspace?._id || '')
  const issuedFor = idOf(root)
  return (patch) => {
    if (idOf(root) !== issuedFor) return false
    root.update(patch)
    return true
  }
}
